import {
  fetchingError,
  selectPeriod,
  selectReportingCurrencyCode,
} from '../../../shared/store/actions';

import {
  FETCHING_TAX_EXPENSE_REPORT_SUCCESS,
  SELECT_VIEW_TYPE,
  SELECT_REPORT_BOOK_TYPE,
  SELECT_REPORT_BOOK_TYPES,
  SELECT_GROUP_TYPE,
  FETCH_TAX_EXPENSE_REPORT,
  FETCH_TAX_EXPENSE_REPORT_FOR_ALL_PILLS,
  FETCH_TAX_EXPENSE_REPORT_FOR_ALL_PILLS_SUCCESS,
  FETCH_TAX_EXPENSE_REPORT_FOR_ALL_PILLS_FAILURE,
  FETCH_DRILL_DETAIL,
  FETCH_DRILL_DETAIL_SUCCESS,
  FETCH_DRILL_DETAIL_FAILURE,
  SELECT_ENTRY_FOR_DRILL_DETAIL,
  SELECT_COMPANY_CODE_TO_FILTER,
  SELECT_CUSTOM_GROUP,
  SELECT_DETAIL_AGGREGATION_TYPE,
  RESET_TAX_EXPENSE_REPORT,
  SELECT_COUNTRY_TAX_TYPE,
  FETCH_COUNTRY_TAX_TYPES,
  FETCH_COUNTRY_TAX_TYPES_SUCCESS,
} from './types';

import {
  fetchReportData as fetchReportDataApi,
  fetchDrillDetailForExport as fetchDrillDetailForExportApi,
  fetchDrillDetail as fetchDrillDetailApi,
  fetchCustomGroupOptions,
  fetchAvailableCountryTaxTypesForPeriod as fetchAvailableCountryTaxTypesForPeriodApi,
} from '../../api';

import { errorNotification } from '../../../shared/store/notificationActions';
import downloadTaxExpenseXlsx from '../downloadTaxExpenseXlsx';
import downloadTaxExpenseCsv from '../downloadTaxExpenseCsv';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../../userData/store/selectors';

import {
  BOOK_TYPES,
  TOTAL_ROW_VALUE,
  TOTAL_FEDERAL_ROW_FOR_US_SUMMARY,
  TOTAL_US_ROW,
} from '../../shared/constants';
import { FUNCTIONAL_CURRENCY } from '@common/constants/sharedConstants';
import { GROUP_TYPES } from '@common/constants/reportsConstants';

import { COLUMNS, taxCurrencyFieldDescription, defaultCurrency, OPTION_ALL, groupTypeValues } from '../constants';
import { calculateDetailsTotals } from './calculateDetailsTotals';
import initialState from './initialState';

const availableBookTypes = Object.values(BOOK_TYPES);

const normalizeSelectedReportBookTypes = selectedReportBookTypes => {
  const normalized = (selectedReportBookTypes || [])
    .filter(bookType => availableBookTypes.includes(bookType));

  return normalized.length ? normalized : [initialState.reportBookType];
};

export const fetchCountryTaxTypes = period => async dispatch => {
  dispatch({ type: FETCH_COUNTRY_TAX_TYPES });

  try {
    const countryTaxTypes = await fetchAvailableCountryTaxTypesForPeriodApi(period.name);
    dispatch({
      type: FETCH_COUNTRY_TAX_TYPES_SUCCESS,
      countryTaxTypes,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchReportDataForTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  if (!(
    period
    && reportBookType
    && groupType
    && reportingCurrencyCode
  )) {
    return;
  }

  const reportingCurrencyCodeToSet = reportingCurrencyCode && reportingCurrencyCode !== taxCurrencyFieldDescription
    ? reportingCurrencyCode
    : defaultCurrency;

  if (useHistorical && groupType === groupTypeValues.CUSTOM_GROUP) {
    dispatch({
      type: RESET_TAX_EXPENSE_REPORT,
    });
    return;
  }

  dispatch({
    type: FETCH_TAX_EXPENSE_REPORT,
    reportingCurrencyCode: reportingCurrencyCodeToSet,
  });

  try {
    const normalizedSelectedReportBookTypes = normalizeSelectedReportBookTypes(selectedReportBookTypes);
    const response = groupType === 'GR' && customGroup === null ? initialState.reportData : await fetchReportDataApi({
      groupBy: groupType,
      period: period.name,
      bookType: reportBookType,
      bookTypes: normalizedSelectedReportBookTypes,
      reportingCurrency: reportingCurrencyCodeToSet,
      useHistorical,
      companyCodeToFilter,
      customGroup,
      countryTaxType,
      generateAsyncDetailsLink,
    });

    const groupOptions = !useHistorical ? await fetchCustomGroupOptions({ periodName: period.name }) : [];
    const customGroupOptions = groupOptions
      ? groupOptions.map(({ groupItem, groupItemDescription }) => ({
        value: groupItem,
        label: groupItemDescription ? groupItemDescription : groupItem,
      }))
      : [];

    dispatch({
      type: FETCHING_TAX_EXPENSE_REPORT_SUCCESS,
      reportData: response[reportBookType] || response,
      reportDataByBookType: response[reportBookType] ? response : { [reportBookType]: response },
      customGroupOptions,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchInitialDataForViewTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  await dispatch(selectPeriod(period));
  if (!useHistorical) {
    await dispatch(fetchCountryTaxTypes(period));
  }
  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter,
    customGroup,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};


export const selectPeriodForViewTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  dispatch(selectPeriod(period));
  if (!useHistorical) {
    await dispatch(fetchCountryTaxTypes(period));
  }
  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter,
    customGroup,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};

export const selectReportBookType = ({
  reportBookType,
  selectedReportBookTypes,
  reportData,
}) => async dispatch => {
  dispatch({
    type: SELECT_REPORT_BOOK_TYPE,
    reportBookType,
    selectedReportBookTypes,
    reportData,
  });
};

export const selectReportBookTypes = selectedReportBookTypes => ({
  type: SELECT_REPORT_BOOK_TYPES,
  selectedReportBookTypes: normalizeSelectedReportBookTypes(selectedReportBookTypes),
});

export const selectGroupType = groupType => ({
  type: SELECT_GROUP_TYPE,
  groupType,
});

export const selectGroupTypeForViewTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  dispatch(selectGroupType(groupType));
  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter: OPTION_ALL.value,
    customGroup,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};

export const selectBookTypeForViewTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  reportDataByBookType = {},
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  const normalizedSelectedReportBookTypes = normalizeSelectedReportBookTypes(selectedReportBookTypes);

  dispatch(selectReportBookType({
    reportBookType,
    selectedReportBookTypes: normalizedSelectedReportBookTypes,
    reportData: reportDataByBookType[reportBookType],
  }));

  if (reportDataByBookType[reportBookType]) {
    return;
  }

  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes: normalizedSelectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter,
    customGroup,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};

export const selectViewTypeForViewTaxExpensePage = viewType => ({
  type: SELECT_VIEW_TYPE,
  viewType,
});

export const selectDetailAggregationForTaxExpensePage = detailAggregationType => ({
  type: SELECT_DETAIL_AGGREGATION_TYPE,
  detailAggregationType,
});

const buildTaxExpenseExportPayload = async ({
  groupType,
  period,
  viewType,
  reportBookType,
  selectedReportBookTypes,
  hasSourceCurrency,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  countryTaxTypeDescription,
  environmentSuffix,
  detailAggregationType,
  useExpandableDetailsList,
}, getState) => {
  const bookTypesToExport = normalizeSelectedReportBookTypes(selectedReportBookTypes)
    .filter(bookType => !useHistorical || bookType !== BOOK_TYPES.US_SUMMARY);

  const reportDataByBookType = await Promise.all(bookTypesToExport.map(async bookType => {
    const reportData = await fetchReportDataApi({
      groupBy: groupType,
      period: period.name,
      bookType,
      reportingCurrency: reportingCurrencyCode,
      useHistorical,
      companyCodeToFilter,
      countryTaxType: bookType === BOOK_TYPES.US_STATE ? countryTaxType : OPTION_ALL.value,
      customGroup,
      detailAggregationType,
    });

    return [bookType, reportData];
  })).then(Object.fromEntries);

  const getTotalColumns = bookType => ({
    [BOOK_TYPES.US_SUMMARY]: [TOTAL_FEDERAL_ROW_FOR_US_SUMMARY, TOTAL_US_ROW],
  })[bookType] || [TOTAL_ROW_VALUE];

  const detailRows = viewType === COLUMNS
    ? await fetchDrillDetailForExportApi({
      useHistorical,
      periodName: period.name,
      reportingCurrency: reportingCurrencyCode,
      groupType,
      companyCodeToFilter,
      customGroup,
      detailAggregationType,
      countryTaxType,
    }).then(details => calculateDetailsTotals(
      details,
      groupType === GROUP_TYPES.CC && companyCodeToFilter && companyCodeToFilter !== 'ALL' ? GROUP_TYPES.CTT : groupType,
      getTotalColumns,
      reportingCurrencyCode === FUNCTIONAL_CURRENCY
    )) : null;

  const currentState = getState();

  return {
    period,
    reportData: reportDataByBookType,
    detailRows,
    viewType,
    reportBookType,
    selectedReportBookTypes: bookTypesToExport,
    groupType,
    groupItem: companyCodeToFilter,
    countryTaxTypeDescription,
    hasSourceCurrency,
    reportingCurrencyCode,
    shouldDisplayZero: displayZeroSelector(currentState),
    hideDecimals: hideDecimalsSelector(currentState),
    negativeMinus: negativeMinusSelector(currentState),
    useHistorical,
    environmentSuffix,
    detailAggregationType,
    countryTaxType,
    useExpandableDetailsList,
  };
};

const runTaxExpenseExport = download => params => async (dispatch, getState) => {
  const { groupType, period } = params;
  if (!(groupType && period)) {
    return;
  }

  dispatch({
    type: FETCH_TAX_EXPENSE_REPORT_FOR_ALL_PILLS,
  });

  try {
    const payload = await buildTaxExpenseExportPayload(params, getState);
    await download(payload);

    dispatch({
      type: FETCH_TAX_EXPENSE_REPORT_FOR_ALL_PILLS_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: FETCH_TAX_EXPENSE_REPORT_FOR_ALL_PILLS_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const exportTaxExpenseDataForAllPills = runTaxExpenseExport(downloadTaxExpenseXlsx);
export const exportTaxExpenseCsvForAllPills = runTaxExpenseExport(downloadTaxExpenseCsv);

export const selectReportingCurrencyForViewTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  await dispatch(selectReportingCurrencyCode(reportingCurrencyCode));
  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter,
    customGroup,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};

export const fetchDrillDetail = ({
  useHistorical,
  periodName,
  reportType,
  reportLineType,
  reportingCurrency,
  groupType,
  groupItem,
  funcCurrency,
  taxCurrency,
  detailAggregationType,
  countryTaxType,
}) => async dispatch => {
  dispatch({ type: FETCH_DRILL_DETAIL });

  try {
    const drillDetailResponse = await fetchDrillDetailApi({
      useHistorical,
      periodName,
      reportType,
      reportLineType,
      reportingCurrency,
      groupType,
      groupItem,
      funcCurrency,
      taxCurrency,
      detailAggregationType,
      countryTaxType,
    });

    const drillDetail = drillDetailResponse.map(detailRow => detailRow.account.startsWith(' - ')
      ? { ...detailRow, account: detailRow.account.substring(3) }
      : detailRow
    );

    dispatch({
      type: FETCH_DRILL_DETAIL_SUCCESS,
      drillDetail,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({ type: FETCH_DRILL_DETAIL_FAILURE });
      return;
    }
    throw error;
  }
};

export const selectEntryForDrillDetail = ({
  countryTaxType,
  columnTitle,
  reportLineType,
  groupItem,
  cellToElevateSelector,
}) => ({
  type: SELECT_ENTRY_FOR_DRILL_DETAIL,
  countryTaxType,
  columnTitle,
  reportLineType,
  groupItem,
  cellToElevateSelector,
});

export const selectCompanyCodeToFilter = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  dispatch({ type: SELECT_COMPANY_CODE_TO_FILTER, companyCodeToFilter: companyCodeToFilter.value });
  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter: companyCodeToFilter.value,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};

export const selectCustomGroup = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  dispatch({ type: SELECT_CUSTOM_GROUP, customGroup: customGroup.value });
  await dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter,
    customGroup: customGroup.value,
    countryTaxType,
    generateAsyncDetailsLink,
  }));
};

export const selectCountryTaxTypeForViewTaxExpensePage = ({
  period,
  reportBookType,
  selectedReportBookTypes,
  groupType,
  reportingCurrencyCode,
  useHistorical,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => async dispatch => {
  dispatch({ type: SELECT_COUNTRY_TAX_TYPE, countryTaxType: countryTaxType && countryTaxType.value });

  return dispatch(fetchReportDataForTaxExpensePage({
    period,
    reportBookType,
    selectedReportBookTypes,
    groupType,
    reportingCurrencyCode,
    useHistorical,
    companyCodeToFilter,
    customGroup,
    countryTaxType: countryTaxType.value,
    generateAsyncDetailsLink,
  }));
};
